/**
 * stripUndefined — deep-clone that removes `undefined` keys at every
 * nesting level. Firestore Web SDK rejects `undefined` even with merge:true
 * (Sprint 16 bug A7); every content-plan write should funnel through this.
 *
 * Preserves null, Timestamp / FieldValue sentinels (non-plain objects are
 * kept as-is), arrays, and nested plain objects.
 */
export function stripUndefined<T>(input: T): T {
  if (input === undefined) return input;
  if (input === null) return input;
  if (Array.isArray(input)) {
    return input.map((v) => stripUndefined(v)) as unknown as T;
  }
  if (typeof input !== 'object') return input;

  // Preserve Firestore sentinels (Timestamp, FieldValue, etc.) — they have
  // non-plain prototypes. A plain object has Object.prototype as its proto.
  const proto = Object.getPrototypeOf(input);
  if (proto !== Object.prototype && proto !== null) return input;

  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(input as Record<string, unknown>)) {
    if (v === undefined) continue;
    out[k] = typeof v === 'object' && v !== null ? stripUndefined(v) : v;
  }
  return out as T;
}
